import {Component, Input, OnInit} from '@angular/core';
import {
  CalendarViewType,
  LeagueCompetitorResult,
  Country,
  EventItem,
  League,
  LeagueEvents,
  LeagueStatus,
  Lookups
} from "@model";
import {buildEventSlug} from "@utility";
import {
  EVENT_CALENDAR_ROUTE,
  EVENT_DETAILS, PRIVATE_EVENT_CALENDAR_ROUTE,
  SERIES_INFO,
  SERIES_LEAGUE_EVENTS
} from "../../../../app-routing.module";
import {ActivatedRoute, Router} from "@angular/router";
import {AbstractComponent} from "@components";
import {TranslocoService} from "@ngneat/transloco";
import {LeagueTemplateEventStatus} from "../../../../model/league/league-template-event-status";
import {AuthenticationService} from "@services";

@Component({
  selector: 'app-league-overview-tab',
  templateUrl: './league-overview-tab.component.html',
  styleUrl: './league-overview-tab.component.scss',
  standalone: false
})
export class LeagueOverviewTabComponent extends AbstractComponent implements OnInit {
  @Input() league?: League;
  @Input() leagueEvents?: LeagueEvents;
  @Input() results: LeagueCompetitorResult[] = [];
  @Input({ required: true }) languages: Lookups[] = [];
  @Input({required: true}) countries: Country[] = [];

  aboutExpanded = false;
  upcomingEvents: EventItem[] = [];
  navigateEventIds = new Set<number>();

  totalNumberOfEvents: number = 0;
  finishedNumberOfEvents: number = 0;

  constructor(private router: Router, private route: ActivatedRoute,
              public translocoService: TranslocoService, public authenticationService: AuthenticationService) {
    super();
  }

  ngOnInit(): void {
    if (!this.leagueEvents?.events) {
      this.totalNumberOfEvents = this.league?.maxNumberOfEvents ?? 0;
    }
    else {
      if (this.league?.roundsEnabled) {
        this.totalNumberOfEvents = this.leagueEvents.totalRounds ?? 0;
        this.finishedNumberOfEvents = this.leagueEvents.finishedRounds ?? 0;
      }
      else {
        this.totalNumberOfEvents = this.leagueEvents.total ?? 0;
        this.finishedNumberOfEvents = this.leagueEvents.finished.length ?? 0
      }
    }
    this.navigateEventIds = new Set((this.leagueEvents?.events ?? []).map(e => e.id!));
    this.upcomingEvents = (this.leagueEvents?.upcoming ?? [])
        .filter(e => e.id !== this.leagueEvents?.nextRound?.id)
        .slice(0, 3);
  }

  private getLeagueSlug(): string | null {
    return this.route.parent?.snapshot.params['slug'] ?? null;
  }

  goToEvents() {
    const slug = this.getLeagueSlug();
    slug
      ? this.router.navigate([`/leagues/details/${slug}/events`])
      : this.router.navigate([SERIES_LEAGUE_EVENTS], {queryParamsHandling: "preserve"});
  }


  goToEventDetailsPage(item: EventItem) {
    if (this.navigateEventIds.has(item.id!)) {
      const baseRoute = this.authenticationService.isAuthenticated()
          ? PRIVATE_EVENT_CALENDAR_ROUTE
          : EVENT_CALENDAR_ROUTE;

      const url = this.router.serializeUrl(
          this.router.createUrlTree([baseRoute, EVENT_DETAILS, buildEventSlug(item.id!, item)])
      );
      window.open(url, '_blank');
    }
  }

  goToInfo() {
    const slug = this.getLeagueSlug();
    slug
      ? this.router.navigate([`/leagues/details/${slug}/info`])
      : this.router.navigate([SERIES_INFO], {queryParamsHandling: "preserve"});
  }

  goToOfficialLeaguePage() {
    if (this.league?.officialWebsite) {
      window.open(this.league!.officialWebsite!, '_blank');
    }
  }

  toggleAbout(): void {
    this.aboutExpanded = !this.aboutExpanded;
  }

  protected readonly CalendarViewType = CalendarViewType;
  protected readonly LeagueStatus = LeagueStatus;
  protected readonly LeagueTemplateEventStatus = LeagueTemplateEventStatus;

  readonly templateStatusBanner: Partial<Record<LeagueTemplateEventStatus, {icon: string, color: string, key: string}>> = {
    [LeagueTemplateEventStatus.ACTIVE]: { icon: 'warning-icon-blue', color: 'color-blue-600', key: 'app.common.details.coming.soon' },
    [LeagueTemplateEventStatus.EXTERNAL]: { icon: 'warning-icon-red', color: 'color-red-600', key: 'league.template.event.status.3' },
    [LeagueTemplateEventStatus.CANCELLED]: { icon: 'warning-icon-red', color: 'color-red-600', key: 'app.common.canceled' },
  };

}
